import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import { useAuth } from '@/lib/auth';
import { useSEO } from '@/seo/hooks';
import { TrackingLink } from '@/lib/marketing';
import { HOME_ROOT_COPY } from '@/lib/marketing/home-root-copy';
import { resolveLegacyHomeAnchor } from '@/lib/marketing/legacy-home-anchors';
import PageLayout from '@/components/askjds/PageLayout';
import { MarketingRootNavbar } from '@/components/marketing/MarketingRootNavbar';
import { FlagGate, useFlagDecision } from '@/components/guards/FlagGate';
import { SectionDivider } from '@/components/home/SectionDivider';
import { Testimonial, TRUSTPILOT_URL } from '@/components/marketing/Testimonial';
import { useIsNativeApp } from '@/components/native';

/**
 * `/` — the homepage, built from the designer's artboards **2a (desktop,
 * 1200px)** and **2b (2a on mobile, 390px)** in "Homepage Directions.dc.html".
 * Founder-confirmed 2a is the chosen concept.
 *
 * ── THIS IS `/` AS OF THE PHASE 2 CUTOVER, 2026-09-11 ──────────────────────
 * It was `/home-preview`, an unlisted founder-review route, and this docblock
 * used to open "THIS IS NOT `/`". The cutover (board `1MsSnZAnseQ4qBbvPUpv`)
 * repointed `/` here, deleted the preview route, deleted
 * `src/components/HomePage.tsx` outright, and moved the product content that
 * page carried to `/law-school/unlimited` and `/law-school`.
 *
 * WHAT MOVED WITH IT, all in the one commit, because each half is silently
 * wrong without the others:
 *   - the NAV: `/` gets MarketingRootNavbar (four items, one anchor);
 *     AskJDSNavbar became the LAW-SCHOOL context nav and its four
 *     `pathname === '/'` assumptions moved to /law-school/unlimited;
 *   - the CRAWLER BODY: scripts/prerender.ts no longer exempts
 *     `type: 'homepage'` from SEO_CONTENT replacement, so `/` composes from
 *     getHomepageContent() instead of a hand-written block in index.html;
 *   - the FAQ JSON-LD: getFAQSchema() maps over this page's own five questions
 *     instead of enumerating HomePage's seven;
 *   - the COPY: the hero, the five tiles, the carry-forward band and the FAQ
 *     live in `src/lib/marketing/home-root-copy.ts`, imported by this page, by
 *     the prerendered payload and by the schema. Three surfaces, one string.
 *
 * ── EIGHT THINGS ARE LOAD-BEARING. Read before editing. ─────────────────────
 *
 * 1. THE DESIGN IS AUTHORITATIVE ON VISUALS, NOT ON CLAIMS.
 *    Where 2a's drawn copy conflicts with what the code actually gates, the
 *    code wins and the deviation is recorded here. Every deviation below was
 *    traced to a symbol, never to a doc — cite the SYMBOL, not a line number.
 *
 * 2. THE FREE TILE. 2a lists a bare "Flashcards" and "Notes & Outlines" under
 *    "Always Free". Three separate corrections:
 *      - "ALWAYS FREE" IS NOW SHIPPED — FOUNDER AUTHORISED 2026-09-09, and the
 *        open question this note used to record is closed. It was withheld
 *        because "always" reads as a promise about the tier's FUTURE, which
 *        nobody had approved. The founder asked for it directly, and it is
 *        paired with a present-tense line ("No trial, no time limit, no credit
 *        card") that says what the tier IS rather than what it will remain.
 *        The guard in HomeVariantBPage.claim-contract.test.ts was narrowed to
 *        match: "free forever" / "will stay free" are STILL banned.
 *        `/law-school` still refuses the phrase — this was approved for the
 *        homepage tile, not site-wide. Do not propagate it there without asking.
 *      - FLASHCARDS ARE NOT FLATLY FREE, AND NOT FLATLY PAID EITHER. The TOOL
 *        is free: `isPremiumFeature('flashcards')` in
 *        `src/hooks/useFeatureAccess.ts` returns FALSE as of 2026-09-11 and
 *        `FlashcardsPageWrapper` is a passthrough, so Study Mode, collections,
 *        subjects and a user's own cards are all free. The gate is PER CARD — an
 *        `is_official` card shows its question and blurs the ANSWER — so premium
 *        DECKS are still not free. Route-gating the tool was the 2ed1d7d8
 *        (2025-08-31) regression, reverted 2026-09-11. "All
 *        flashcards free" is a banned claim and so is "flashcards are premium"
 *        — gate the CONTENT, not the TOOL
 *        (.claude/rules/free-tier-value-claims.md guardrail 2). The approved
 *        phrasing is used verbatim.
 *      - THE OUTLINE LIBRARY IS FREE. `/outlines/library` is
 *        `FlagGate flag="outlines"` with no entitlement check. The premium
 *        `outline_iq` key is Outline IQ, the AI ANALYSIS tool at
 *        `/outlines/analyses` — a different product, named on the Unlimited
 *        tile instead. Conflating the two has already been an error in three
 *        independent places; do not make it four.
 *    Ask JDS is 30 messages/month (`FREE_MESSAGE_LIMIT` in
 *    `src/lib/subscription.ts`). "Unlimited free chat" is banned.
 *
 * 3. "VIDEO LIBRARY" ON THE $20 TILE IS CORRECT, AND WAS VERIFIED, NOT ASSUMED.
 *    `isPremiumFeature('courses')` returns TRUE, so courses/video belong on the
 *    paid tile. Named "Courses and video lessons" to match the product.
 *
 * 4. THE OFFICIAL NCBE PACKS TILE IS ON THE KILL SWITCH.
 *    `nextgen-official-questions` IS the atomic kill switch for every licensed
 *    surface (.claude/rules/ncbe-licensed-content.md §III.C/§III.F), and
 *    `/bar/ncbe-nextgen-questions` redirects to `/bar` when it is OFF. An ungated
 *    card advertising a route that bounces the visitor is a loop that looks
 *    like a bug — `BarStagePage` gates its matching card for exactly this
 *    reason, and gating BOTH is scoping the fix by concept rather than by file
 *    (.claude/rules/propagate-the-fix.md §2). Verified 2026-09-07 against the
 *    Unleash admin API: development ON, production OFF.
 *
 *    `<FlagGate>` is used rather than a hand-rolled `useFlag`, because THERE
 *    ARE FOUR FLAG STATES AND THE FOURTH IS PERMANENT. When
 *    VITE_UNLEASH_URL/VITE_UNLEASH_FRONTEND_TOKEN are absent at build time,
 *    `src/main.tsx` omits <FlagProvider> entirely; `flagsReady` is then pinned
 *    false and `flagsError` pinned null forever, so any `flagsReady ||
 *    flagsError` guard is false for the life of the page. FlagGate closes that
 *    by leading on the build-time `unleashEnabled` constant. Do not replace it
 *    with a local hook that re-derives the wrong three-state version.
 *
 * 5. THE COURSE INCLUDES THE PACKS. THIS IS SETTLED — DO NOT WITHHOLD IT.
 *    Founder decision 2026-09-07, re-confirmed 2026-09-09: "is it that we
 *    include ncbe questions as part of the course? if so, that's fine because
 *    this is our largest priced and biggest margin product."
 *    This note used to say the includes-chain was "still NOT shipped here,
 *    deliberately" on §IV.B caution. That was MY call, made AFTER the founder
 *    had already decided, and it cost three builds and three repeat questions.
 *    §IV.B is a royalty TRIGGER, not a prohibition — $15/set/individual, $135
 *    cap, triggered by provided ACCESS (§IV.E). Paying it is a commercial
 *    decision and it has been made.
 *    STILL PROHIBITED, and this is the whole of it: bundling the Packs into
 *    JDS Unlimited ($20/mo) or NextGen Practice ($499) — large populations, not
 *    priced to carry the royalty — and any claim the Packs are free or a bonus.
 *    The guard in HomeVariantBPage.claim-contract.test.ts is scoped to exactly
 *    those two products; see its BUNDLING_PATTERNS docblock.
 *    THE REAL COMPLIANCE WORK IS NOT COPY: every Course purchase granting Pack
 *    access must write an immutable per-(user, set) access row for the
 *    semi-annual certified report (rule §6, STORY zTqMBHDdYskiZHerXtJ7 / T6).
 *
 * 6. FIVE TILES. NOT FOUR — AND THIS NOTE ONCE SAID THE OPPOSITE.
 *    Until 2026-09-08 this note read "FIVE TILES BECAME FOUR", described the
 *    Packs and Course collapsing into one "What's next" tile, and attributed it
 *    to "Founder direction". That was WRONG on every clause and the attribution
 *    was mine, not theirs: the founder picked the five-tile concept and
 *    explicitly REJECTED dropping to four by burying the free tile ("it's easy
 *    to miss the 'always free' tier"). They never said to cut from the other
 *    end. They then asked twice why only four products were showing.
 *    All five products are tiles. The two that are not open yet use PathTile's
 *    `muted` weight. The rail is deliberately ASYMMETRIC (2 law school, 3 bar)
 *    because the stages hold unequal product counts — splitting the row 50/50
 *    by stage is what made every bar tile narrower than every law-school tile.
 *    Kept as a correction rather than a clean rewrite: this is the file's stated
 *    contract and the first thing the next editor reads, so it should show that
 *    it once asserted the reverse.
 *
 * 7. MOBILE IS THE DESIGNER'S OWN 2b, NOT AN INVENTED RESPONSIVE GUESS.
 *    2b keeps every element of 2a and re-flows it: the horizontal rail becomes
 *    stage GROUPS stacked vertically, each opening with a dot + label + a rule
 *    that runs to the right edge; tiles go full-width one per row; the two
 *    law-school tiles switch their bullet lists to a TWO-COLUMN grid (with the
 *    long last item spanning both); the bar tiles keep single-column bullets;
 *    "Includes" collapses from a stacked list to an inline baseline row; and
 *    the carry-forward "Elsewhere" strip goes from 4-across with one shared
 *    caption to a 2×2 grid with a per-tile caption. All of that is reproduced
 *    below at the `sm`/`lg` breakpoints rather than re-imagined.
 *
 * 8. CARRY-FORWARD HAS A CEILING AND THIS PAGE STATES IT.
 *    2a's band says work carries into bar prep and stops there. Knowledge and
 *    progress DO carry; NextGen exam-readiness and format scores DO NOT (they
 *    are gated to native NextGen practice by the signal_processor source
 *    filter). Approved: "your knowledge and progress carry over", "you never
 *    start cold". Banned: "your readiness is already built". The band therefore
 *    carries the designer's argument plus the ceiling sentence.
 *
 * 9. FOUR SECTIONS ARE INHERITED FROM THE OLD HOMEPAGE, NOT DRAWN IN 2a.
 *    This was a SPEC ERROR, not a build failure: the original brief listed ten
 *    "required deviations from 2a", which read as the complete spec, and the
 *    inheritance requirement lived only in the board task. The first build was
 *    2a-faithful and therefore shipped without any of them. They are:
 *      - NON-SERVICES ("I'm Proud to Say I Do Not Offer") — the sharpest brand
 *        asset on the old homepage and the only section that positions the
 *        COMPANY against the category rather than selling a SKU. 2a is all SKU.
 *      - ABOUT (Cris A.) — company-level trust. It attaches to the company, so
 *        it cannot live on a tier page; every tier page would need its own copy.
 *      - The COMPANY-LEVEL FAQ SUBSET — see note 10.
 *      - The CLOSING CTA BAND (`id="payoff"`) — merged in AS the closing band
 *        rather than added beside one. 2a ends on the rail with no closer; this
 *        page previously ended on the section then called PROOF (now
 *        SCHOOLS) with no second CTA at all.
 *    The first two are RENDERED FROM THEIR EXISTING COMPONENTS
 *    (`src/components/home/{NonServicesSection,AboutSection}.tsx`), lazily, the
 *    way HomePage.tsx already does. Not copied — a copy is two sources of truth
 *    for founder-approved copy (.claude/rules/propagate-the-fix.md §3), and the
 *    cutover deletes HomePage's usage, not the components.
 *    CONSEQUENCE FOR THE GUARD: `visibleCopy` only sees literals in the file it
 *    is handed, so copy inside those two components is INVISIBLE to a scan of
 *    this page. The claim-contract suite therefore scans them as separate files
 *    rather than assuming the page's own scan covers them.
 *
 * 10. THE FAQ IS A SUBSET, AND THE SPLIT IS ALREADY WRITTEN DOWN ELSEWHERE.
 *    `/law-school/unlimited` states the cut map in its own source: the
 *    company-level questions "stay on the homepage per the cut map — this page
 *    answers what you get and what it costs." So the five here are the company
 *    half, and the two product-level ones HomePage.tsx still carries
 *    ("Do I need a credit card to start?", "How much does JD Simplified cost?")
 *    are DELIBERATELY ABSENT — `/law-school/unlimited` already answers both
 *    ("Do I need a credit card to start?", "How much does Unlimited cost?",
 *    "Can I cancel?"). Duplicating them puts two competing answer sets in front
 *    of one crawler, which is the thing the namespace split exists to stop.
 *
 * 11. TWO CLAIMS INSIDE THE PORTED FAQ WERE FLAGGED, AND BOTH ARE NOW RESOLVED.
 *    The brief was PORT, do not rewrite, so both shipped VERBATIM for one review
 *    round rather than being quietly corrected — a silently reworded
 *    founder-approved claim is indistinguishable from an approved one, and this
 *    is a noindex preview whose whole purpose is founder review. Both then came
 *    back with answers, and both are applied here.
 *      (a) PRODUCT NAME — RENAMED. Five occurrences of "NextGen Prep" are now
 *          "NextGen Practice". The rename was approved 2026-09-07 (Product
 *          Naming, Family A); it survived in this copy only because the sweep
 *          was scoped to a migration task's acceptance criteria that had not run
 *          yet. Distinct from the LATER, narrower ruling that the short form
 *          carries no JDS prefix ("NextGen Practice", never "JDS NextGen
 *          Practice") — that one is about the prefix and does not reach a
 *          different name, which is why this needed its own answer.
 *          SCOPED TO THIS FILE ON PURPOSE. "NextGen Prep" is still live in
 *          HomePage.tsx and LawSchoolStagePage.tsx (×2) and is NOT swept here:
 *          the repo-wide rename is filed separately so it lands as one
 *          concept-scoped change instead of three windows each fixing their own
 *          corner (.claude/rules/propagate-the-fix.md §2 — scope by CONCEPT,
 *          which here means one sweep, not one file at a time).
 *          One knock-on: "inside NextGen Prep's own practice" would have become
 *          "NextGen Practice's own practice", so that clause reads "in NextGen
 *          Practice itself". Same claim, different words — noted at the site.
 *      (b) "ALL OF STUDY MODE" — REWRITTEN, and this one was never a founder
 *          call. The inherited answer said Unlimited "adds everything — all of
 *          Study Mode, …", which puts Study Mode behind the plan. The verified
 *          boundary: `isPremiumFeature('flashcards')` returns FALSE and
 *          `FlashcardsPageWrapper` is a passthrough (2026-09-11 revert of the
 *          2ed1d7d8 route gate); the gate is the per-card `is_official` ANSWER
 *          blur — so the TOOL is free and the premium DECKS are not. That is exactly
 *          the gate-the-content guardrail (.claude/rules/free-tier-value-claims.md
 *          guardrail 2), and the approved sentence was already four inches up
 *          the page on the free tile. The answer now agrees with it.
 *          A GUARD WAS ADDED IN THE SAME CHANGE, because the reason this
 *          survived review is that NOTHING FIRED: it is not one of the banned
 *          strings, so both the page and the suite were green while the two
 *          sentences described different products. STUDY_MODE_GATING_PATTERNS in
 *          the claim-contract suite now bans the SHAPE — any plan that "adds" or
 *          "includes" or "unlocks" Study Mode — rather than the one string.
 *
 * ── THE CUTOVER IS DONE. WHAT THIS SECTION USED TO SAY, AND WHAT IT SAYS NOW ─
 *
 * It carried a 7-item CUTOVER CHECKLIST and a NOINDEX removal condition. Every
 * item shipped in the cutover commit, and the checklist is replaced by what a
 * future reader actually needs: what would break if any of it were reverted
 * piecemeal.
 *
 * (1) `/` POINTS HERE. `noindex` is gone from the useSEO call below. Those two
 *     are ONE change: an indexable preview competing with `/` was the reason for
 *     the flag, and a `noindex` on `/` now removes the page earning 94% of all
 *     organic clicks from Google with no error and no failing build. Pinned in
 *     HomeVariantBPage.claim-contract.test.ts, in both directions.
 *
 * (2) THE `/home-preview` ROUTE IS DELETED, along with its entry in
 *     INTENTIONALLY_NOT_PRERENDERED (src/seo/routes.ts) and its
 *     NEVER_PROMOTABLE classification in prerender-allowlist-ratchet.test.ts.
 *     The ratchet's stale-registry check reds the build if one is left behind
 *     without the other. The route was DELETED rather than promoted, and that
 *     distinction is the whole of its old registry entry: promoting it would
 *     have prerendered and sitemapped a second copy of the homepage.
 *
 * (3) THE index.html #seo-content BLOCK IS REWRITTEN — and it is now the
 *     SPA-FALLBACK body only. scripts/prerender.ts stopped exempting
 *     `type: 'homepage'`, so for `/` itself that block no longer ships at all;
 *     the crawler-visible body comes from getHomepageContent() in
 *     src/seo/payloads.ts. It is kept in step anyway so the two do not read as
 *     contradicting each other, and because it IS what a no-JS visitor gets on
 *     any non-prerendered route. Do not edit copy there expecting `/` to change.
 *
 * (4) `getFAQSchema()` NOW MAPS OVER THIS PAGE'S FIVE QUESTIONS. It used to
 *     enumerate HomePage.tsx's seven as a hand-written object literal, two of
 *     which had already moved to /law-school/unlimited — a structured-data /
 *     visible-content mismatch waiting for the repoint. Both sides read
 *     HOME_ROOT_COPY.faqs now, so they cannot disagree; the guard is in
 *     src/seo/__tests__/homepage-jsonld.test.ts.
 *
 * (5) THE PACKS BLURB IS RECONCILED AGAINST /bar/course, which was item (6) of
 *     the old checklist. That item warned the Packs row's "A separate purchase."
 *     line had a KNOWN EXPIRY, because the founder's 2026-09-07 reversal put the
 *     Packs inside the NextGen Course. Resolved: the line was already removed
 *     2026-09-09, and BarCoursePage states the same arrangement from the other
 *     side — the Packs are part of the Course, and remain separately purchasable
 *     at /bar/ncbe-nextgen-questions. Checked at cutover: nothing on this page
 *     contradicts that, and the Course tile's `includes` names the Packs, which
 *     is the approved claim. The compliance obligation is unchanged and is NOT
 *     copy: every Course purchase granting Pack access must write an immutable
 *     per-(user, set) access row for the semi-annual certified report.
 */

/**
 * The two inherited company-level sections, rendered from the components that
 * already own them rather than re-typed here (docblock note 9). Lazy + Suspense
 * mirrors HomePage.tsx: both sit well below the fold and neither is needed for
 * first paint.
 */
const NonServicesSection = lazy(() =>
  import('@/components/home/NonServicesSection').then((m) => ({ default: m.NonServicesSection }))
);
const AboutSection = lazy(() =>
  import('@/components/home/AboutSection').then((m) => ({ default: m.AboutSection }))
);

const SectionPlaceholder = ({ minHeight }: { minHeight: string }) => (
  <div aria-hidden="true" style={{ minHeight }} />
);

const LEARN_MORE = 'Learn more';

/** Blue = law school. Orange = bar. Matches 2a's rail and dot colours. */
type Stage = 'law-school' | 'bar';

const STAGE_INK: Record<Stage, string> = {
  'law-school': 'text-[#00178E] dark:text-[#5b74e8]',
  bar: 'text-[#F37022]',
};

const STAGE_DOT: Record<Stage, string> = {
  'law-school': 'bg-[#00178E] dark:bg-[#5b74e8] shadow-[0_0_0_2px_#00178E] dark:shadow-[0_0_0_2px_#5b74e8]',
  bar: 'bg-[#F37022] shadow-[0_0_0_2px_#F37022]',
};

const STAGE_RULE: Record<Stage, string> = {
  'law-school': 'bg-[#00178E] dark:bg-[#5b74e8]',
  bar: 'bg-[#F37022]',
};

/**
 * The stage marker from 2a/2b: a ringed dot plus an uppercase label.
 *
 * On DESKTOP (2a) the marker sits ON the rail and masks it with the page
 * background, so the line appears to start after the label. On MOBILE (2b)
 * there is no rail, and the marker instead grows its own rule out to the right
 * edge — hence the `lg:hidden` trailing rule rather than two separate markers.
 */
function StageMarker({ stage, label }: { stage: Stage; label: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <span
        aria-hidden="true"
        className={`w-5 h-5 lg:w-6 lg:h-6 rounded-full flex-none border-4 border-white dark:border-gray-950 ${STAGE_DOT[stage]}`}
      />
      <span
        className={`relative z-10 bg-white dark:bg-gray-950 pr-3 text-xs font-bold uppercase tracking-[0.06em] ${STAGE_INK[stage]}`}
      >
        {label}
      </span>
      <span aria-hidden="true" className={`lg:hidden flex-1 h-0.5 ${STAGE_RULE[stage]}`} />
    </div>
  );
}

interface PathTileProps {
  /** Small muted line above the product name — price or availability. */
  kicker: string;
  name: string;
  bullets: readonly string[];
  /**
   * 2b switches the law-school tiles to two bullet columns on a 390px screen
   * and leaves the bar tiles single-column. Faithful to the artboard.
   */
  twoColumnBulletsOnMobile?: boolean;
  /**
   * Rendered in the bordered "Includes" row. One product name or several —
   * 2a's Course tile lists three, and the founder decided 2026-09-07 that the
   * Official NCBE Question Packs ARE part of the Course ("we include ncbe
   * questions as part of the course ... this is our largest priced and biggest
   * margin product").
   */
  includes?: string | readonly string[];
  href: string;
  /** UTM `content` for TrackingLink — one per tile, so the rail is measurable. */
  trackingContent: string;
  /** The orange emphasis treatment 2a gives the NextGen Practice tile. */
  emphasis?: boolean;
  /**
   * Lower-contrast card for a product that is not open yet — dashed border, no
   * hover lift. Border matches the standard tile's gray-200/gray-800 rather
   * than going a step darker: the first version used gray-300/gray-700, which
   * measured as MORE prominent than the tiles it was meant to sit behind
   * (browser-verified 2026-09-08). The de-emphasis is the dash pattern and the
   * absent fill, not a heavier line. Designer's call 2026-09-08: the Packs and Course were collapsed
   * into a borderless "What's next" column and "read as footnotes rather than
   * products"; the fix is "the same card treatment at lower contrast ... so the
   * row reads as five products on one path." Same COMPONENT, so the row is one
   * visual family; only the weight differs.
   */
  muted?: boolean;
  children?: ReactNode;
}

function PathTile({
  kicker,
  name,
  bullets,
  twoColumnBulletsOnMobile = false,
  includes,
  href,
  trackingContent,
  emphasis = false,
  muted = false,
  children,
}: PathTileProps) {
  return (
    <TrackingLink
      href={href}
      campaign="home_variant_b_path"
      content={trackingContent}
      className={[
        'group flex flex-col gap-3 rounded-2xl border p-5 sm:p-6 h-full min-h-[44px]',
        'transition-all duration-200',
        // No lift on a muted tile: the hover promise should match what the
        // click delivers, and these two are a waitlist and a coming-soon page.
        muted ? '' : 'hover:-translate-y-1 hover:shadow-xl',
        muted
          ? 'border-dashed border-gray-200 dark:border-gray-800 bg-transparent hover:border-gray-300 dark:hover:border-gray-700'
          : emphasis
            ? 'border-[1.5px] border-[#F37022] bg-[#fff8f4] dark:bg-[#F37022]/10 hover:shadow-[#F37022]/20'
            : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 hover:border-[#00178E] dark:hover:border-[#5b74e8]',
      ].join(' ')}
    >
      <div>
        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400">{kicker}</div>
        <div className="mt-0.5 text-lg sm:text-xl font-bold tracking-tight text-black dark:text-white">
          {name}
        </div>
      </div>

      <ul
        className={[
          'flex-1 text-sm leading-relaxed text-gray-600 dark:text-gray-400 list-disc pl-5',
          twoColumnBulletsOnMobile
            ? 'grid grid-cols-2 gap-x-4 gap-y-1 lg:flex lg:flex-col lg:gap-1'
            : 'flex flex-col gap-1',
        ].join(' ')}
      >
        {bullets.map((b, i) => (
          <li
            key={b}
            className={
              twoColumnBulletsOnMobile && i === bullets.length - 1
                ? 'col-span-2 sm:col-span-1'
                : undefined
            }
          >
            {b}
          </li>
        ))}
      </ul>

      {children}

      {includes && (
        <div
          className={[
            // Stacked, not inline. Founder 2026-09-09: match the NextGen
            // Practice tile, which only stacked because its value wrapped.
            // flex-col makes that deliberate rather than a width accident.
            'flex flex-col items-start gap-y-1 border-t pt-2.5',
            emphasis ? 'border-[#f3d9c8] dark:border-[#F37022]/30' : 'border-gray-200 dark:border-gray-800',
          ].join(' ')}
        >
          <span className="text-[0.6875rem] font-bold uppercase tracking-[0.06em] text-gray-400 dark:text-gray-500">
            Includes
          </span>
          {Array.isArray(includes) ? (
            <ul className="flex flex-col gap-y-0.5">
              {includes.map((item) => (
                <li
                  key={item}
                  className="flex gap-x-1.5 text-[0.8125rem] font-semibold text-black dark:text-white"
                >
                  <span aria-hidden="true" className="text-gray-400 dark:text-gray-500">
                    &bull;
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          ) : (
            <span className="text-[0.8125rem] font-semibold text-black dark:text-white">{includes}</span>
          )}
        </div>
      )}

      <span
        className={`text-sm font-semibold ${emphasis ? 'text-[#F37022]' : 'text-[#00178E] dark:text-[#5b74e8]'}`}
      >
        {LEARN_MORE}
        <span aria-hidden="true" className="ml-1 inline-block transition-transform group-hover:translate-x-1">
          &rarr;
        </span>
      </span>
    </TrackingLink>
  );
}

/**
 * The Studicata partner mark, in its LOCKED form.
 *
 * Lowercase and bold, founder-confirmed (board activity YgGOukvT1v5hMrjNuXJo).
 * Not sentence-cased, and not folded into surrounding prose — an earlier draft
 * of this page shipped it as plain sentence-initial text inside a blurb, which
 * is a different mark. House reference: BarCoursePage.tsx renders exactly this.
 * The mark attaches to NextGen Practice and the Course ONLY — never to the
 * Official NCBE Question Packs, which are NCBE's.
 */
const StudicataMark = () => (
  <strong className="font-semibold">{HOME_ROOT_COPY.studicataMark}</strong>
);


export function HomeVariantBPage() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // Pure Client v1: no dollar prices and no checkout affordance in the native
  // shell (docs/mobile-app/STORE-PURCHASE-STRATEGY.md). The tiles link to
  // marketing pages rather than checkout, so only the price KICKERS swap.
  const isNative = useIsNativeApp();

  /**
   * The COMPANY half of the old homepage FAQ, ported verbatim (docblock notes
   * 10 + 11). The two product-level questions HomePage.tsx also carries are
   * deliberately not here — `/law-school/unlimited` owns those.
   *
   * Verbatim includes the parts that are now questionable: "NextGen Prep" in
   * three answers, and "all of Study Mode" in the second. Both are flagged in
   * note 11 and both need a founder ruling. Do not quietly fix either — a
   * reworded founder-approved claim looks identical to an approved one, and
   * this preview exists so a human can see the conflict.
   */
  /**
   * The COMPANY half of the old homepage FAQ — five questions.
   *
   * HOISTED OUT OF THIS FILE at the 2026-09-11 cutover and now imported from
   * src/lib/marketing/home-root-copy.ts. That is not tidying: `getFAQSchema()`
   * in src/seo/schema-helpers.ts maps over the SAME array to build the FAQPage
   * JSON-LD for `/`, and the prerendered crawler body composes from it too.
   * While these were three hand-written literals in three files, the schema
   * enumerated SEVEN questions belonging to a page that no longer exists and
   * nothing in the repo compared them (board JMnyYLzSUtBx8BbVbRSl).
   *
   * The cut map and the two claim rulings that shaped these answers are
   * recorded at the copy module, beside the strings they govern.
   *
   * Pure Client v1: the "Is it really free?" answer names dollar prices, so it
   * carries a native variant. JSON-LD and the prerendered body always take the
   * WEB form — neither has a native consumer.
   */
  const companyFaqs = HOME_ROOT_COPY.faqs.map((faq) => ({
    q: faq.question,
    a: isNative && faq.answerNative ? faq.answerNative : faq.answer,
  }));

  /**
   * THE GRID MUST COUNT THE TILES THAT ACTUALLY RENDER, NOT THE ONES WRITTEN.
   *
   * The Packs tile is behind the `nextgen-official-questions` kill switch,
   * which is OFF in production. A static 5-column grid with a 3-column bar span
   * therefore left an EMPTY third column in prod and ran the orange rail 234px
   * (one full column + one gap) past the last card — measured in a browser
   * 2026-09-08, 34% of the bar stage as bare rail over nothing. It read as a
   * missing tile, which is exactly what this file's rail comment calls "the
   * tell", and the arithmetic could not catch it because it was derived against
   * the flag-ON tree.
   *
   * `useFlagDecision` is the SAME four-state decision FlagGate makes, exported
   * so there is one implementation — a local `useFlag` here would re-derive the
   * broken three-state version this file's note 4 warns about. Unresolved
   * counts as OFF, which matches production and keeps the common path
   * reflow-free.
   */
  const packsVisible = useFlagDecision('nextgen-official-questions').on;
  const communityVisible = useFlagDecision('community-board').on;
  const barTileCount = packsVisible ? 3 : 2;
  const totalTiles = 2 + barTileCount;

  /**
   * Width of a rail spanning `n` of `totalTiles` equal columns in a 20px-gap
   * grid. Derived, never typed by hand — the two literals this replaced
   * (calc(40% - 12px) / calc(60% - 8px)) were correct for five columns and
   * wrong for four, which is how the rail came to overshoot by a whole column
   * in production.
   *   col = (100% - (T-1)*20) / T
   *   w   = n*col + (n-1)*20
   * T=5: n=2 -> 40% - 12px, n=3 -> 60% - 8px.  T=4: n=2 -> 50% - 10px (both).
   * The two rails plus the 20px between the stages sum to exactly 100%.
   */
  const railWidth = (n: number) =>
    `calc(${(n / totalTiles) * 100}% - ${(n * (totalTiles - 1) * 20) / totalTiles - (n - 1) * 20}px)`;

  // NOINDEX IS GONE — this is `/` now. It was unconditional while the page was
  // an unlisted preview at /home-preview; the removal condition the docblock
  // stated was "flip it in the same cutover commit that points `/` at this
  // component", and this is that commit. `/` is `type: 'homepage'` in
  // SEO_ROUTES with `prerender: true`, so the prerendered head is written from
  // getPageSEO() and THIS call governs only the HYDRATED one. Two different
  // functions in two different files; nothing in the type system relates them.
  //
  // THIS COMMENT ONCE CLAIMED "the two agree because both read the same strings
  // from the copy module" AND THAT WAS FALSE WHEN WRITTEN. getHomepageSEO() in
  // src/seo/payloads.ts still hardcoded its own pair, so the built
  // `_prerendered/home` served crawlers "Law School & Bar Prep, Simplified" and
  // a description opening "Stop guessing what to study" — a hinge the copy
  // module explicitly bans for this surface — while this call rendered the
  // approved pair to anyone running JS. The comment described the intent and
  // was read as a fact.
  //
  // They DO both read the module now. The guarantee is src/seo/__tests__/
  // homepage-jsonld.test.ts, which pins getPageSEO('/')'s title, description,
  // og:* and twitter:* to HOME_ROOT_COPY — not this sentence.
  useSEO({
    title: HOME_ROOT_COPY.seoTitle,
    description: HOME_ROOT_COPY.seoDescription,
    image: '/images/og-jds-root-2026.png',
    url: 'https://jdsimplified.com/',
  });

  /**
   * FORWARD THE OLD HOMEPAGE'S SECTION ANCHORS.
   *
   * `/#caselaw`, `/#notes`, `/#chat`, `/#practice`, `/#library` and `/#pricing`
   * were live nav targets on this URL until today and are cached in the wild —
   * in blog pages, in the SEO baseline's inbound-anchor list, and in anything
   * anyone ever pasted. This page renders none of them, and an unresolvable
   * fragment is not an error: the browser finds no element and silently leaves
   * the visitor at the top of a page that looks nothing like the link they
   * clicked.
   *
   * A FRAGMENT NEVER REACHES THE SERVER, so no 301 and no url-map rule can do
   * this — `GET /#caselaw` is `GET /` on the wire. This effect is the only layer
   * that can, which is also why there is nothing to add to
   * infra/b1-url-map-live.yaml for them.
   *
   * `replace: true` so the dead anchor does not become a back-button trap.
   * The table and its "stay put on anything unrecognised" rule live in
   * src/lib/marketing/legacy-home-anchors.ts, tested against the real ids
   * /law-school/unlimited renders.
   */
  useEffect(() => {
    const target = resolveLegacyHomeAnchor(location.hash);
    if (target) navigate(target, { replace: true });
  }, [location.hash, navigate]);

  return (
    // THE ROOT NAVBAR, not PageLayout's default. PageLayout defaults to
    // AskJDSNavbar, which since the cutover is the LAW-SCHOOL context navbar —
    // Features ▾ / Outlines / Pricing, all resolving against
    // /law-school/unlimited. Inheriting it here would put a product nav on the
    // page whose entire job is to route between products, and its scroll-spy
    // would run against a section list this page does not render.
    <PageLayout navbar={<MarketingRootNavbar />}>
      <div className="bg-white dark:bg-gray-950">
        {/* ── HERO (2a) ─────────────────────────────────────────────────────
            Eyebrow, H1, subhead and a single primary CTA, centred over a soft
            radial wash.

            THE H1 IS WHAT ARTBOARD 2a DREW, and that is the whole point of the
            comment. It previously read "Stop guessing what to study." on the
            strength of the copy ruling logged to board task
            MW8rjkuxDGDyoc7PNkFu — a ruling that was, and still is, in `review`
            status and was NEVER signed off by the founder. Founder correction
            2026-09-08: "this isnt the headline we agreed on."

            An unapproved recommendation is not a decision, and the approved
            artifact here is the artboard. Restored 2026-09-08.

            There is no duplication with `/law-school/unlimited`: that page is
            law-school-scoped and takes "Your companion for thriving in law
            school." (founder's own wording, same session). This page spans 1L
            THROUGH the bar, which is what "and the bar exam" carries. */}
        <section
          id="top"
          className="relative overflow-hidden px-4 pt-16 pb-12 text-center md:pt-24 md:pb-16"
          style={{ scrollMarginTop: '6rem' }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_80%_at_50%_0%,#fff1ea_0%,transparent_70%)] dark:bg-[radial-gradient(60%_80%_at_50%_0%,rgba(243,112,34,0.12)_0%,transparent_70%)]"
          />
          <div className="relative z-10 mx-auto box-border max-w-3xl">
            <span className="mb-4 inline-block text-xs font-semibold uppercase tracking-[0.08em] text-[#F37022] md:text-sm">
              {HOME_ROOT_COPY.eyebrow}
            </span>
            <h1 className="text-[2.125rem] font-extrabold leading-[1.08] tracking-tight text-black dark:text-white md:text-5xl lg:text-[3.5rem] lg:leading-[1.05]">
              {HOME_ROOT_COPY.heading}
            </h1>
            <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-gray-600 dark:text-gray-400 md:mt-5 md:text-lg">
              {HOME_ROOT_COPY.subheading}
            </p>
            <div className="mt-7 flex justify-center md:mt-8">
              <TrackingLink
                href={user ? '/dashboard' : '/auth?tab=signup'}
                campaign="home_variant_b_hero"
                content={user ? 'open_dashboard' : 'create_free_account'}
                className="inline-flex min-h-[44px] w-full items-center justify-center rounded-full bg-[#F37022] px-7 py-3.5 text-[0.9375rem] font-semibold text-white shadow-md transition-all hover:bg-[#E35D10] hover:shadow-lg sm:w-auto"
              >
                {user ? 'Open your dashboard' : 'Create your free account'}
              </TrackingLink>
            </div>
          </div>
        </section>

        {/* ── THE PATH (2a rail / 2b stacked stage groups) ──────────────────
            DESKTOP: two stage columns side by side, two tiles each, with a
            two-tone rail running behind the stage markers — blue across the law
            school half, orange across the bar half.
            MOBILE (2b): the rail disappears, the stage groups stack, each
            marker grows its own rule to the right edge, and tiles go
            full-width one per row. */}
        <section
          id="path"
          className="px-4 pb-16 pt-4 md:pb-20"
          style={{ scrollMarginTop: '6rem' }}
        >
          <div className="relative mx-auto box-border max-w-6xl">
            {/* The rail. Desktop only — 2b has no continuous line.
                WIDTHS ARE DERIVED FROM THE TILE GRID, NOT PICKED. All five
                tiles share one lg:grid-cols-5 with a uniform 20px gap
                (lg:gap-5 outer, lg:gap-5 inner), so every tile is exactly
                (W - 80) / 5 wide. Law school owns 2 columns + 1 gap =
                0.4W - 12; bar owns 3 columns + 2 gaps = 0.6W - 8. Those plus
                the 20px between the stages sum to exactly W.
                If you change the column count or any gap, re-derive both — a
                rail that no longer ends where its tiles end is the tell. */}
            <div
              aria-hidden="true"
              className="absolute left-0 top-[11px] hidden h-0.5 bg-[#00178E] dark:bg-[#5b74e8] lg:block"
              style={{ width: railWidth(2) }}
            />
            <div
              aria-hidden="true"
              className="absolute right-0 top-[11px] hidden h-0.5 bg-[#F37022] lg:block"
              style={{ width: railWidth(barTileCount) }}
            />

            {/* FIVE columns, not two. The stages are UNEQUAL (2 products vs 3) so
                splitting the row 50/50 by STAGE made every bar tile narrower
                than every law-school tile — founder 2026-09-08: "the product
                tiles should all share room equally. the bar items are too
                narrow because law school is taking up half."
                Now the grid is per-TILE and the stages span it: 2 and 3. */}
            <div
              className={`relative grid grid-cols-1 gap-10 lg:gap-5 ${
                packsVisible ? 'lg:grid-cols-5' : 'lg:grid-cols-4'
              }`}
            >
              {/* ── STAGE: law school ─────────────────────────────────── */}
              <div className="flex flex-col gap-4 lg:col-span-2">
                <StageMarker stage="law-school" label={HOME_ROOT_COPY.stages.lawSchool} />
                {/* Stacks until lg, matching the bar stage. It USED to go 2-up at
                    sm while the bar stage stayed 1-up, which put the two stages
                    on different layouts at the same breakpoint — an asymmetry
                    no artboard asks for and a side effect of the third bar tile
                    landing later. 2b is drawn at 390px and stacks both, so the
                    sm-to-lg range was never designed; stacking is the reading
                    that keeps the two stages consistent. */}
                <div className="grid flex-1 grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-5">
                  {/* Copy from HOME_ROOT_COPY (src/lib/marketing/home-root-copy.ts).
                      The rulings that shaped these bullets live there, beside
                      the strings — notably that a bare "Flashcards" is CORRECT
                      because it names the TOOL, which is free
                      (FlashcardsPageWrapper is a passthrough since the 2026-09-11
                      revert; the gate is the per-card is_official ANSWER blur),
                      while the paid tile names the premium CONTENT. The two tiles
                      TOGETHER are the gate-the-content split; it does not have
                      to fit in one sentence on one tile. */}
                  <PathTile
                    kicker={HOME_ROOT_COPY.tiles.free.kicker}
                    name={HOME_ROOT_COPY.tiles.free.name}
                    href={HOME_ROOT_COPY.tiles.free.href}
                    trackingContent="tile_free"
                    twoColumnBulletsOnMobile
                    bullets={[
                      ...HOME_ROOT_COPY.tiles.free.bullets,
                      // Founder 2026-09-09: promoted from a trailing sentence to
                      // two bullets. STILL FLAG-GATED — community-board is ON in
                      // development and OFF in PRODUCTION, and the founder said
                      // "Don't enable yet", so an ungated bullet would advertise
                      // a feature a prod visitor cannot reach. Appended via the
                      // same useFlagDecision read the tile grid uses, because
                      // `bullets` is a prop array and <FlagGate> cannot wrap an
                      // array element.
                      //
                      // NOT hoisted into the copy module: the module feeds the
                      // PRERENDERED body, which is generated at build time with
                      // no flag context, so a flag-gated string there would ship
                      // unconditionally to crawlers.
                      ...(communityVisible ? ['Community', 'Study groups'] : []),
                    ]}
                  >
                    <p className="text-[0.8125rem] text-gray-500 dark:text-gray-400">
                      {HOME_ROOT_COPY.tiles.free.note}
                    </p>
                  </PathTile>

                  <PathTile
                    kicker={
                      isNative
                        ? HOME_ROOT_COPY.tiles.unlimited.kickerNative
                        : HOME_ROOT_COPY.tiles.unlimited.kicker
                    }
                    name={HOME_ROOT_COPY.tiles.unlimited.name}
                    href={HOME_ROOT_COPY.tiles.unlimited.href}
                    trackingContent="tile_unlimited"
                    twoColumnBulletsOnMobile
                    includes={HOME_ROOT_COPY.tiles.unlimited.includes}
                    bullets={HOME_ROOT_COPY.tiles.unlimited.bullets}
                  />
                </div>
              </div>

              {/* ── STAGE: bar exam ───────────────────────────────────── */}
              <div
                className={`flex flex-col gap-4 ${
                  packsVisible ? 'lg:col-span-3' : 'lg:col-span-2'
                }`}
              >
                <StageMarker stage="bar" label={HOME_ROOT_COPY.stages.bar} />
                {/* THREE tiles, and NO sm:grid-cols-2 — that is the point.
                    A 2-up grid holding three tiles strands the third alone on a
                    second row in the middle of the stage. The first version of
                    this shipped with sm:grid-cols-2 still in place under a
                    comment claiming the problem was solved; it was solved only
                    at lg. Stacked below lg, 3-up at lg. */}
                <div
                  className={`grid flex-1 grid-cols-1 gap-4 lg:gap-5 ${
                    packsVisible ? 'lg:grid-cols-3' : 'lg:grid-cols-2'
                  }`}
                >
                  {/* "From $499", never "$499 one-time" — $499 buys ONE exam
                      cycle and later cycles cost more. The kicker's native
                      variant drops the figure entirely (Pure Client v1). */}
                  <PathTile
                    kicker={
                      isNative
                        ? HOME_ROOT_COPY.tiles.practice.kickerNative
                        : HOME_ROOT_COPY.tiles.practice.kicker
                    }
                    name={HOME_ROOT_COPY.tiles.practice.name}
                    href={HOME_ROOT_COPY.tiles.practice.href}
                    trackingContent="tile_practice"
                    emphasis
                    includes={HOME_ROOT_COPY.tiles.practice.includes}
                    bullets={HOME_ROOT_COPY.tiles.practice.bullets}
                  />

                  {/* 2a's tiles 4 and 5, as REAL TILES. They shipped collapsed
                      into one borderless "What's next" column and the designer
                      called it 2026-09-08: they "read as footnotes rather than
                      products". Founder asked twice why only four products were
                      showing. Both are now PathTile like every other product —
                      same component, `muted` weight — so the rail reads as five
                      products on one path, which is what the founder chose
                      variant B for.

                      The kicker carries availability instead of price because
                      neither is purchasable: "Coming soon" and "March 2027".
                      Bullets stay to TWO each — these are the least-committed
                      products on the page and should not out-weigh the tiles
                      that take money today. */}
                  {/* See docblock note 4 — this tile is on the kill switch. */}
                  <FlagGate flag="nextgen-official-questions">
                    {/* "A separate purchase" is deliberately ABSENT from these
                        bullets — removed by the founder 2026-09-09, and the
                        compliance reasoning is recorded at the copy module: §IV.B
                        bans claiming the Packs are INCLUDED with anything; it
                        does not require affirmatively stating "separate" on every
                        surface. Do NOT replace it with any "includes" phrasing. */}
                    <PathTile
                      kicker={HOME_ROOT_COPY.tiles.packs.kicker}
                      name={HOME_ROOT_COPY.tiles.packs.name}
                      href={HOME_ROOT_COPY.tiles.packs.href}
                      trackingContent="tile_official_questions"
                      muted
                      bullets={HOME_ROOT_COPY.tiles.packs.bullets}
                    />
                  </FlagGate>
                  <PathTile
                    kicker={HOME_ROOT_COPY.tiles.course.kicker}
                    name={HOME_ROOT_COPY.tiles.course.name}
                    href={HOME_ROOT_COPY.tiles.course.href}
                    trackingContent="tile_course"
                    muted
                    /* 2a as drawn. FOUNDER DECISION 2026-09-07, re-confirmed
                       2026-09-09: the Packs ARE part of the Course. I withheld
                       this list from three builds on §IV.B caution AFTER that
                       decision had already been made, and re-raised it as an
                       open question. It was not open.
                       WHAT §IV.B ACTUALLY MEANS HERE: the royalty is a COST of
                       the bundle, not a bar on it — $15 per set per individual,
                       $135 lifetime cap, triggered by provided ACCESS (§IV.E).
                       That is a commercial call and the founder has made it.
                       WHAT IT OBLIGES THE BUILD TO DO, and this is the real
                       follow-through: every Course purchase that grants Pack
                       access must write an immutable per-(user, set) access row
                       for the semi-annual certified report. Tracked on the
                       Packs build (STORY zTqMBHDdYskiZHerXtJ7 / T6 SKU work) —
                       this tile is a claim, that ledger is the compliance. */
                    includes={HOME_ROOT_COPY.tiles.course.includes}
                    bullets={HOME_ROOT_COPY.tiles.course.bullets}
                  >
                    <StudicataMark />
                  </PathTile>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── SCHOOLS ───────────────────────────────────────────────────────
            2a has no proof of any kind — its reviewed weakness. This strip and
            the testimonial below About are the answer; both already exist and
            are reused rather than re-authored.

            FOUNDER DIRECTIVE 2026-09-08: no "PROOF" eyebrow label, and the
            schools stay HIGH on the page while the testimonial moves down
            below About. So this section carries no SectionDivider at all — the
            heading "Where our students come from" is its own label, and a
            second one above it was redundant.

            FOUNDER-LOCKED content: the school list, the "42+" aggregate, the
            "Where our students come from" heading and the non-affiliation
            footnote are lifted verbatim from HomePage.tsx and may not be
            reworded here. The framing is SIGNUP-only ("come from") on purpose —
            Yale and Georgetown signed up, so any usage verb ("used by",
            "trusted by") beside those names would be false. */}
        <section
          id="schools"
          className="border-t border-gray-200 bg-white px-4 py-16 dark:border-gray-800 dark:bg-gray-950 md:py-20"
          style={{ scrollMarginTop: '6rem' }}
        >
          {/* No `mt-12 border-t pt-10` here any more: that rule existed to
              separate this block from the testimonial that used to sit above
              it. With the testimonial moved below About, this is the section's
              first and only child and the separator would draw a stray line. */}
          <div className="mx-auto box-border max-w-4xl text-center">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 md:text-xl">
              Where our students come from
            </h2>
            <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">From 42+ law schools</p>
            <ul className="mt-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-base text-gray-700 dark:text-gray-200">
              {[
                'Yale',
                'Georgetown',
                'UC Berkeley',
                'UCLA',
                'USC',
                'Boston University',
                'Iowa',
                'Pepperdine',
              ].map((school, i) => (
                <li key={school} className="flex items-center gap-x-3">
                  {i > 0 && (
                    <span aria-hidden="true" className="text-gray-300 dark:text-gray-600">
                      ·
                    </span>
                  )}
                  <span>{school}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-[0.6875rem] leading-relaxed text-gray-400 dark:text-gray-500">
              JD Simplified is not affiliated with or endorsed by these institutions.
            </p>
          </div>
        </section>

        {/* ── ONE ACCOUNT (2a's ELSEWHERE / HERE band) ──────────────────────
            The designer's contrast graphic, plus the carry-forward CEILING
            sentence 2a does not carry. See docblock note 8: knowledge and
            progress carry; NextGen exam-readiness and format scores do not. */}
        <section
          id="one-account"
          className="px-4 pb-16 md:pb-20"
          style={{ scrollMarginTop: '6rem' }}
        >
          <div className="mx-auto box-border grid max-w-6xl gap-8 rounded-3xl bg-[#00178E] p-6 text-white sm:p-10 lg:grid-cols-2 lg:gap-14 lg:p-14">
            <div>
              <div className="mb-3 text-[0.6875rem] font-bold uppercase tracking-[0.08em] text-[#FF8C47] md:mb-4 md:text-xs">
                {HOME_ROOT_COPY.carryForward.eyebrow}
              </div>
              <h2 className="text-2xl font-extrabold leading-[1.15] tracking-tight md:text-[2.125rem]">
                {HOME_ROOT_COPY.carryForward.heading}
              </h2>
              <p className="mt-4 max-w-lg text-[0.9375rem] leading-relaxed text-[#c7cdea] md:text-base">
                {HOME_ROOT_COPY.carryForward.body}
              </p>
              <p className="mt-3 max-w-lg text-sm leading-relaxed text-[#8d98d6]">
                {HOME_ROOT_COPY.carryForward.ceiling}
              </p>
            </div>

            <div className="flex flex-col gap-4 md:gap-5">
              <div className="flex flex-col gap-2">
                <div className="text-[0.6875rem] font-bold uppercase tracking-[0.08em] text-[#8d98d6]">
                  Elsewhere
                </div>
                {/* 2b: 2×2 with a per-tile caption. 2a: 4-across with ONE
                    shared caption underneath. Both are rendered — the per-tile
                    caption is hidden from `sm` up, where the shared line
                    appears instead. */}
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {['Study aids', 'Outlines', 'Bar review', 'Question bank'].map((label) => (
                    <div
                      key={label}
                      className="rounded-lg border border-dashed border-[#5c6bc4] p-2.5 text-xs leading-snug sm:text-center"
                    >
                      <div className="font-semibold text-white">{label}</div>
                      <div className="text-[#c7cdea] sm:hidden">nothing carries over</div>
                    </div>
                  ))}
                </div>
                <div className="hidden text-xs text-[#8d98d6] sm:block">
                  Nothing carries over between them.
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <div className="text-[0.6875rem] font-bold uppercase tracking-[0.08em] text-[#FF8C47]">
                  Here
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-white p-4 text-black">
                  <div className="flex items-center gap-2.5">
                    <img
                      src="/images/logo/jd-simplified-icon.svg"
                      alt=""
                      aria-hidden="true"
                      width={26}
                      height={26}
                      className="h-6 w-6"
                    />
                    <span className="text-sm font-bold">One account</span>
                  </div>
                  <div className="flex gap-1.5 text-[0.6875rem] font-semibold">
                    {['1L', '2L', '3L'].map((y) => (
                      <span key={y} className="rounded-full bg-[#eef1fb] px-2 py-1 text-[#00178E]">
                        {y}
                      </span>
                    ))}
                    <span className="rounded-full bg-[#fff1ea] px-2 py-1 text-[#F37022]">Bar</span>
                  </div>
                </div>
              </div>

              {/* The section's own CTA. Two reasons it lives HERE and not only
                  in the hero and the closing band:

                  1. It closes THIS argument. The band's claim is that your work
                     carries forward — the action that claim implies is "start
                     now, so there is something to carry", which is a different
                     pitch from the hero's and aimed at a reader who is not yet
                     thinking about the bar.
                  2. It fills the only long CTA dead zone on the page. After the
                     product tiles the next button is the closing payoff band,
                     four sections later — past Non-Services, About, the
                     testimonials and the whole FAQ.

                  `mt-auto` pins it to the bottom of the right column so it
                  absorbs the height difference against the left column rather
                  than leaving a half-column of empty navy under the Here card.
                  Below `lg` the grid is a single column with auto-sized rows,
                  so there is no free space and the auto margin resolves to 0 —
                  inert on tablet as well as phone, not only on phone. */}
              <div className="mt-auto flex flex-col gap-3 pt-2">
                <p className="text-sm leading-relaxed text-[#c7cdea]">
                  The earlier you start, the more there is to carry.
                </p>
                <TrackingLink
                  href={user ? '/dashboard' : '/auth?tab=signup'}
                  campaign="home_variant_b_one_account"
                  content={user ? 'open_dashboard' : 'create_free_account'}
                  className="inline-flex min-h-[44px] w-full items-center justify-center rounded-full bg-[#F37022] px-7 py-3.5 text-[0.9375rem] font-semibold text-white shadow-md transition-all hover:bg-[#E35D10] hover:shadow-lg sm:w-auto sm:self-start"
                >
                  {user ? 'Open your dashboard' : 'Create your free account'}
                </TrackingLink>
              </div>
            </div>
          </div>
        </section>

        {/* ── NON-SERVICES (inherited) ──────────────────────────────────────
            "I'm Proud to Say I Do Not Offer". Docblock note 9: the only
            section on the page that positions the COMPANY against the category
            instead of selling a SKU, which is exactly what 2a's five-tile rail
            cannot do. Rendered from the existing component — its copy is
            founder-approved and lives there, not here. */}
        {/* 600px, not HomePage's 280px. MEASURED, not copied: on mobile the
            six cards stack, each `p-8` with a 56px icon tile, past the 16-unit
            header block — so a 280px reservation guarantees a visible jump when
            the chunk lands. HomePage's number was carried over from a different
            layout; reusing it would have been cargo-culting a placeholder. */}
        <Suspense fallback={<SectionPlaceholder minHeight="600px" />}>
          <NonServicesSection />
        </Suspense>

        {/* ── ABOUT (inherited) ─────────────────────────────────────────────
            Cris A. — "a legal educator who teaches strategy, not just
            doctrine." Company-level trust, so it belongs on the company page
            and on no tier page. Brand guardrail: legal EDUCATOR, never
            attorney or lawyer — the component owns that wording. */}
        {/* 420px: `py-20` (160px) plus the 256px portrait, which stacks above
            the prose on mobile. Same reasoning as above. */}
        <Suspense fallback={<SectionPlaceholder minHeight="420px" />}>
          <AboutSection />
        </Suspense>

        {/* ── TESTIMONIALS ──────────────────────────────────────────────────
            FOUNDER DIRECTIVE 2026-09-08, two parts. (a) Moved here from the
            schools section and stripped of its "PROOF" eyebrow. (b) One quote
            was not enough — "there were a couple on the jds page" — so BOTH
            founder-approved testimonials now ship, side by side.

            Both are RETAINED VERBATIM from HomePage.tsx, quote, name and
            credential alike. Tacila's "LL.M., Ohio State Moritz '17" is the
            only credential either carries and it is the stronger of the two
            proofs, so it travels with the quote rather than being trimmed for
            symmetry.

            Placed AFTER Cris introduces herself: both quotes are about being
            taught by a person ("like having a tutor on call at 2am",
            "getting feedback"), so they read as corroboration of About rather
            than as unattributed claims arriving before the reader knows who
            teaches. The schools strip carries the top-of-page proof instead —
            it is a scale claim, which is what belongs next to the tiles.

            `variant="compact"` on both: `card` is sized for a full-width row
            and would overflow a half-width column. Grid, not flex, so the two
            cards match height regardless of quote length.

            No SectionDivider and no border-t — see the SCHOOLS note. Separation
            is the blue-50 AboutSection above going to white here. */}
        <section className="bg-white px-4 py-12 dark:bg-gray-950 md:py-16">
          <div className="mx-auto grid box-border max-w-5xl gap-6 md:grid-cols-2 md:gap-8">
            <Testimonial
              variant="compact"
              quote="JD Simplified gave me actual structure. Ask JDS was like having a tutor on call at 2am when I was stuck on a rule."
              name="Vincent"
              photoSrc="/images/testimonials/vincent.jpeg"
              photoAlt="Vincent"
              trustpilotUrl={TRUSTPILOT_URL}
            />
            <Testimonial
              variant="compact"
              quote="Uploading my outlines and getting feedback made a huge difference in my preparation."
              name="Tacila F. De Lima"
              credentials={"LL.M., Ohio State Moritz ’17"}
              photoSrc="/images/testimonials/tacila.jpg"
              photoAlt="Tacila F. De Lima"
              trustpilotUrl={TRUSTPILOT_URL}
            />
          </div>
        </section>

        {/* ── FAQ (inherited, subset) ───────────────────────────────────────
            Company-level questions only — see docblock note 10 for the cut map
            and note 11 for the two claims carried verbatim under protest.
            Native <details>/<summary>: accessible, keyboard-operable, and
            works with no JS. NO FAQPage JSON-LD here — `getFAQSchema()` is
            injected by HomePage.tsx for `/` and still describes seven
            questions; reconciling it is cutover item (7), and having exactly
            one injection site is what makes that a single edit. */}
        {/* pt-0, NOT the page's usual pt-16/md:pt-20 — and it is arithmetic, not
            taste. SectionDivider carries its own `my-20`, whose whole job is to
            separate itself from what precedes it, so a top padding here is the
            SECOND thing doing that job. Stacked, the gap between the last
            testimonial and the FAQ rule was pb-16 (4rem) + pt-20 (5rem) +
            my-20 (5rem) = 14rem on desktop, against only 9rem above the
            testimonials (About's pb-20 + this band's pt-16). The quotes read as
            orphaned, with far more air below them than above.

            With pt-0 the divider's margin is the only top spacing: 4rem + 5rem
            = 9rem desktop, 3rem + 5rem = 8rem mobile — matching the gap above
            in both cases. (The mt-20 margin-collapses through this edge since
            there is no padding or border to stop it; padding does not collapse,
            so the testimonial band's pb still adds. Same total either way.)

            KEEP pb-16/md:pb-20: the bottom of this section faces the payoff
            band, which has no divider of its own to supply the spacing. */}
        <section
          id="faq"
          className="overflow-x-hidden bg-white px-4 pt-0 pb-16 dark:bg-gray-950 md:pb-20"
          style={{ scrollMarginTop: '6rem' }}
        >
          {/* This section MUST stay white. SectionDivider's label chip is
              `bg-white dark:bg-gray-900` (SectionDivider.tsx), so on a tinted
              host the chip renders as a pale rectangle floating on the tint
              rather than as a break in the rule. This constraint used to be
              recorded on the section formerly called PROOF and left the file
              with it; it is written here because this is now the page's only
              SectionDivider. (Three live grey placements already exist
              elsewhere — HomePage.tsx and LawSchoolStagePage.tsx — pre-dating
              this page and tracked separately.) */}
          <SectionDivider id="faq-divider" label="FAQ" className="mb-12" />
          <div className="mx-auto mb-10 box-border max-w-4xl text-center">
            <h2 className="text-2xl font-bold text-black dark:text-white md:text-4xl">
              Frequently asked questions
            </h2>
          </div>
          <div className="mx-auto box-border max-w-3xl space-y-4">
            {companyFaqs.map((item) => (
              <details
                key={item.q}
                className="group rounded-xl border border-gray-200 bg-gray-50 px-5 py-4 dark:border-gray-700 dark:bg-gray-900 [&_summary::-webkit-details-marker]:hidden"
              >
                <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-between gap-4 text-base font-semibold text-black dark:text-white md:text-lg">
                  <span>{item.q}</span>
                  <ArrowRight
                    aria-hidden="true"
                    className="h-5 w-5 flex-shrink-0 text-[#F37022] transition-transform duration-200 group-open:rotate-90"
                  />
                </summary>
                <p className="mt-3 text-base leading-relaxed text-gray-600 dark:text-gray-400 md:text-lg">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* ── PAYOFF (inherited) — THE closing band ─────────────────────────
            "From panic to perspective. From outlines to outcomes." Merged in AS
            the closer rather than beside one: 2a ends on the rail with no
            closing CTA, and this page previously ended on the section then
            called PROOF (now SCHOOLS), so the hero
            button was the only conversion affordance below a full-page scroll.
            The tracking `content` is kept as `payoff_start_free` so the two
            homepages' closers are comparable across the cutover; the `campaign`
            is this page's own, which is how the variant is attributable. */}
        <section
          id="payoff"
          className="border-t border-gray-200 bg-white px-4 py-16 dark:border-gray-800 dark:bg-gray-950 md:py-20"
          style={{ scrollMarginTop: '6rem' }}
        >
          <div className="mx-auto box-border max-w-4xl text-center">
            <h2 className="mb-6 text-2xl font-bold text-black dark:text-white md:text-4xl">
              From panic to perspective.{' '}
              <span className="whitespace-nowrap">From outlines to outcomes.</span>
            </h2>
            <p className="mb-8 text-xl text-gray-600 dark:text-gray-400">
              JD Simplified is a complete system that helps you think like a lawyer while staying
              sane like a human. Understand more, retain more, and finally feel ahead of your
              syllabus instead of buried under it.
            </p>
            <TrackingLink
              href={user ? '/dashboard' : '/auth?tab=signup'}
              campaign="home_variant_b_payoff"
              content={user ? 'payoff_open_dashboard' : 'payoff_start_free'}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-[#F37022] px-6 py-3 font-medium text-white shadow-md transition-all hover:bg-[#E35D10] hover:shadow-lg"
            >
              {user ? 'Open your dashboard' : 'Start free today'}
              <ArrowRight aria-hidden="true" className="h-5 w-5" />
            </TrackingLink>
          </div>
        </section>
      </div>
    </PageLayout>
  );
}

export default HomeVariantBPage;
